// All inline styles for the terminal UI. Lifted from view-terminal.jsx and
// typed as React.CSSProperties. We keep them as plain objects (no CSS-in-JS
// runtime) — matches the prototype, easy to grep, no extra deps.

import type { CSSProperties } from "react";

type S = Record<string, CSSProperties>;

export const COLORS = {
  bg: "#0a0e0c",
  bgSurface: "#0c1410",
  text: "#e8f3ed",
  textDim: "#7a8a82",
  textDimmer: "#5a6a62",
  textGhost: "#3a4a42",
  border: "#1a2a22",
  borderHi: "#2a3a32",
  accent: "#e8ff5c",
  up: "#7ef0a8",
  down: "#ff8888",
} as const;

export const FONT_MONO =
  '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace';
export const FONT_CONDENSED =
  '"Roboto Condensed", "Helvetica Neue Condensed", "Arial Narrow", sans-serif';

export const tStyles: S = {
  root: {
    width: "100%",
    height: "100%",
    background: COLORS.bg,
    color: COLORS.text,
    fontFamily: FONT_MONO,
    fontSize: 12,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  headerBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 14px",
    borderBottom: `1px solid ${COLORS.border}`,
    background: COLORS.bgSurface,
  },
  brand: { display: "flex", alignItems: "center", gap: 8 },
  logoMark: { color: COLORS.accent, fontSize: 12 },
  logoText: { fontWeight: 700, letterSpacing: 1.5, color: COLORS.text, fontSize: 12 },
  logoSlash: { color: COLORS.textGhost },
  logoSub: { color: COLORS.textDim, fontSize: 11 },
  headerMeta: { display: "flex", alignItems: "center", gap: 10, color: COLORS.textDim },
  metaItem: { fontSize: 11 },
  metaPipe: { color: COLORS.border },
  metaLive: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    color: COLORS.accent,
    fontSize: 11,
    letterSpacing: 1,
  },
  metaDot: { fontSize: 6, animation: "mxPulse 1.5s ease-in-out infinite" },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 14px",
    borderBottom: `1px solid ${COLORS.border}`,
  },
  tabGroup: { display: "flex", gap: 0 },
  tab: {
    background: "transparent",
    border: "none",
    padding: "7px 12px",
    color: COLORS.textDimmer,
    fontFamily: "inherit",
    fontSize: 11,
    letterSpacing: 1,
    cursor: "pointer",
    borderBottom: "2px solid transparent",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  tabActive: { color: COLORS.accent, borderBottom: `2px solid ${COLORS.accent}` },
  tabDisabled: {
    color: COLORS.textGhost,
    cursor: "not-allowed",
    opacity: 0.5,
  },
  tabKey: { color: COLORS.textGhost, fontSize: 9 },
  controlsRight: { display: "flex", gap: 10, alignItems: "center" },
  segGroup: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 2,
  },
  segLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: COLORS.text,
    padding: "0 8px",
    letterSpacing: 1.2,
    borderRight: `1px solid ${COLORS.border}`,
    alignSelf: "stretch",
    display: "flex",
    alignItems: "center",
    background: "#0e1814",
  },
  seg: {
    background: "transparent",
    border: "none",
    padding: "5px 9px",
    color: COLORS.textDim,
    fontFamily: "inherit",
    fontSize: 11,
    letterSpacing: 0.5,
    cursor: "pointer",
  },
  segActive: { background: COLORS.border, color: COLORS.accent },
  segDisabled: { color: COLORS.textGhost, cursor: "not-allowed" },
  cmdLaunch: {
    background: COLORS.bgSurface,
    border: `1px solid ${COLORS.border}`,
    color: COLORS.textDim,
    padding: "5px 10px",
    fontFamily: "inherit",
    fontSize: 11,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    borderRadius: 2,
  },
  kbd: {
    border: `1px solid ${COLORS.borderHi}`,
    padding: "0 5px",
    borderRadius: 2,
    color: COLORS.accent,
    fontSize: 10,
  },
  main: {
    flex: 1,
    display: "grid",
    gridTemplateColumns: "240px 1fr",
    gap: 0,
    overflow: "hidden",
  },
  rail: {
    borderRight: `1px solid ${COLORS.border}`,
    borderLeft: `1px solid ${COLORS.border}`,
    background: COLORS.bgSurface,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
  },
  railHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 12px 6px",
    borderBottom: `1px dashed ${COLORS.border}`,
  },
  railTitle: { color: COLORS.up, fontWeight: 600, letterSpacing: 1, fontSize: 11 },
  railTitleDown: { color: COLORS.down, fontWeight: 600, letterSpacing: 1, fontSize: 11 },
  railTitleWatch: { color: COLORS.accent, fontWeight: 600, letterSpacing: 1, fontSize: 11 },
  railTitleStat: { color: COLORS.textDim, fontWeight: 600, letterSpacing: 1, fontSize: 11 },
  railCount: { color: COLORS.textDimmer, fontSize: 10 },
  railList: { padding: "4px 6px 8px" },
  railRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    padding: "5px 6px",
    color: COLORS.text,
    fontFamily: "inherit",
    fontSize: 11,
    cursor: "pointer",
    borderRadius: 2,
  },
  railIdx: { color: COLORS.textGhost, fontSize: 10, width: 18 },
  railStar: { color: COLORS.accent, width: 18, fontSize: 10 },
  railName: {
    flex: 1,
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  railVelUp: { color: COLORS.up, fontSize: 10 },
  railVelDown: { color: COLORS.down, fontSize: 10 },
  statsBox: { padding: "4px 12px 12px" },
  statRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "3px 0",
    fontSize: 11,
    borderBottom: `1px dashed ${COLORS.border}`,
  },
  statK: { color: COLORS.textDim, letterSpacing: 0.5 },
  statV: { color: COLORS.text, fontVariantNumeric: "tabular-nums" },
  center: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    background: COLORS.bg,
  },
  centerHeader: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 12px",
    borderBottom: `1px solid ${COLORS.border}`,
  },
  centerTitle: { color: COLORS.text, fontWeight: 600, letterSpacing: 1, fontSize: 11 },
  centerSub: { color: COLORS.textDimmer, fontSize: 10 },
  treemapBox: {
    flex: 1,
    padding: 0,
    minHeight: 0,
    position: "relative",
    overflow: "hidden",
  },
  tableBox: {
    flex: 1,
    padding: "6px 0",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  tableHead: {
    display: "grid",
    gridTemplateColumns: "36px 1fr 110px 60px 70px 130px",
    padding: "4px 12px",
    borderBottom: `1px solid ${COLORS.border}`,
    color: COLORS.textDimmer,
    fontSize: 10,
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  thNum: { textAlign: "right" },
  thSpark: { textAlign: "right" },
  tableBody: { flex: 1, overflow: "auto", padding: "2px 0" },
  tr: {
    display: "grid",
    gridTemplateColumns: "36px 1fr 110px 60px 70px 130px",
    padding: "5px 12px",
    alignItems: "center",
    cursor: "pointer",
    borderBottom: `1px dashed #11201a`,
  },
  trCursor: { background: "#0e1814" },
  trSelected: { background: COLORS.border, borderColor: COLORS.accent },
  tdRank: { color: COLORS.textGhost, fontSize: 10 },
  tdName: { fontWeight: 500 },
  tdNum: { textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#cdd9d3" },
  tdSpark: { paddingLeft: 18 },
  scrub: {
    padding: "8px 14px",
    borderTop: `1px solid ${COLORS.border}`,
    background: COLORS.bgSurface,
  },
  scrubHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  scrubLabel: { color: COLORS.textDimmer, letterSpacing: 1, fontSize: 10 },
  scrubValue: { color: COLORS.accent, fontSize: 11 },
  scrubInput: { width: "100%", accentColor: COLORS.accent, height: 4 },
  scrubTicks: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 3,
    color: COLORS.textGhost,
    fontSize: 10,
  },
  footer: {
    padding: "5px 14px",
    borderTop: `1px solid ${COLORS.border}`,
    color: COLORS.textDim,
    display: "flex",
    gap: 16,
    fontSize: 10,
    letterSpacing: 0.5,
    background: COLORS.bgSurface,
    alignItems: "center",
  },
  kbdInline: {
    border: `1px solid ${COLORS.border}`,
    padding: "0 4px",
    borderRadius: 2,
    color: COLORS.accent,
    fontSize: 9,
    marginRight: 3,
  },

  drillBackdrop: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
  },
  drill: {
    width: 700,
    maxHeight: "88%",
    background: COLORS.bgSurface,
    border: `1px solid ${COLORS.border}`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  drillHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    borderBottom: `1px solid ${COLORS.border}`,
  },
  drillTicker: { color: COLORS.accent, fontWeight: 700, letterSpacing: 1 },
  drillName: { color: COLORS.text },
  drillCat: { color: COLORS.textDimmer, fontSize: 11 },
  drillWatch: {
    marginLeft: "auto",
    background: "transparent",
    border: `1px solid ${COLORS.border}`,
    color: COLORS.accent,
    padding: "4px 10px",
    fontFamily: "inherit",
    fontSize: 10,
    cursor: "pointer",
    letterSpacing: 1,
  },
  drillClose: {
    background: "transparent",
    border: "none",
    color: COLORS.textDim,
    cursor: "pointer",
    fontSize: 14,
  },
  drillStats: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    borderBottom: `1px solid ${COLORS.border}`,
  },
  drillStat: {
    padding: "12px 14px",
    borderRight: `1px solid ${COLORS.border}`,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  drillStatLabel: { color: COLORS.textDimmer, fontSize: 9, letterSpacing: 1 },
  drillStatValue: {
    color: COLORS.text,
    fontSize: 18,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
  },
  drillSparkBox: { padding: "12px 14px", borderBottom: `1px solid ${COLORS.border}` },
  drillSparkHead: {
    display: "flex",
    justifyContent: "space-between",
    color: COLORS.textDimmer,
    fontSize: 10,
    letterSpacing: 1,
    marginBottom: 6,
  },
  drillBody: { padding: "12px 14px", overflow: "auto" },
  drillEmpty: {
    padding: "30px 14px",
    color: COLORS.textDim,
    fontSize: 11,
    textAlign: "center" as const,
  },
  drillButton: {
    background: "transparent",
    border: `1px solid ${COLORS.borderHi}`,
    color: COLORS.accent,
    padding: "8px 16px",
    fontFamily: "inherit",
    fontSize: 11,
    cursor: "pointer",
    letterSpacing: 1,
    marginTop: 12,
  },

  cmdBackdrop: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    paddingTop: "12vh",
    zIndex: 20,
  },
  cmdPanel: {
    width: 600,
    background: COLORS.bgSurface,
    border: `1px solid ${COLORS.borderHi}`,
    boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
  },
  cmdInputRow: {
    display: "flex",
    alignItems: "center",
    padding: "12px 14px",
    borderBottom: `1px solid ${COLORS.border}`,
    gap: 10,
  },
  cmdPrompt: { color: COLORS.accent, fontWeight: 700 },
  cmdInput: {
    flex: 1,
    background: "transparent",
    border: "none",
    outline: "none",
    color: COLORS.text,
    fontFamily: "inherit",
    fontSize: 14,
  },
  cmdHint: { color: COLORS.textDimmer, fontSize: 10, letterSpacing: 1 },
  cmdResults: { maxHeight: 400, overflow: "auto", padding: "4px 0" },
  cmdEmpty: { padding: "14px 16px", color: COLORS.textDimmer, fontSize: 11 },
  cmdRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "7px 14px",
    background: "transparent",
    border: "none",
    color: COLORS.text,
    fontFamily: "inherit",
    fontSize: 12,
    cursor: "pointer",
    textAlign: "left",
  },
  cmdRowActive: { background: COLORS.border },
  cmdRowKind: { color: COLORS.accent, width: 10 },
  cmdRowLabel: { flex: 1 },
  cmdRowHint: {
    color: COLORS.textDimmer,
    fontSize: 10,
    letterSpacing: 1,
    textTransform: "uppercase",
  },

  // Banner across the top center for empty/loading states
  emptyBanner: {
    margin: "20px",
    padding: "24px",
    border: `1px dashed ${COLORS.border}`,
    color: COLORS.textDim,
    fontSize: 12,
    lineHeight: 1.6,
    textAlign: "center" as const,
  },
  emptyTitle: { color: COLORS.text, fontWeight: 600, fontSize: 13, letterSpacing: 1, marginBottom: 8 },
};

export const fmtRaw = (n: number): string => n.toLocaleString("en-US");
